// MOS — What the signed-in person may reach, in the browser.
//
// Two sources, in this order:
//   1. The CATALOGUE, resolved from the profile on the employee record. This
//      needs no network, so the nav renders correctly on the first paint —
//      no flash of a menu somebody is not allowed to see, and no spinner in
//      front of the whole app while an access call lands.
//   2. GET /access/me, which adds the two things only the database knows: the
//      person's own exceptions and any deltas on their profile. It arrives a
//      moment later and narrows or widens what step 1 assumed.
//
// This is a convenience, never the guard. Every gate exists on the server too
// (requirePermission), because a permission enforced only in React is a
// suggestion. Hiding a board here is about not showing somebody a door they
// cannot open — the lock is at the other end.

import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { projectId } from '../utils/supabase/info';
import {
  can as canInCatalog,
  canSeePayPeople,
  moneyLensFor,
  profileDef,
  profileForEmployee,
  resolveLevel,
  subtreeLevel,
  type AccessGrantSource,
  type MoneyLens,
  type PermLevel,
  type ProfileKey,
} from '../utils/permissionCatalog';

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e3a97ae5`;

interface RemoteAccess {
  overrides: Record<string, PermLevel>;
  profileGrants: Record<string, PermLevel>;
  moneyLens: MoneyLens;
  canPayPeople: boolean;
}

export interface AccessContextValue {
  /** The resolved inputs — pass this to any catalogue helper. */
  source: AccessGrantSource;
  profile: ProfileKey;
  /** The legacy 1/2/3, still what 726 server routes ask about. */
  tier: 1 | 2 | 3;
  moneyLens: MoneyLens;
  canSeePay: boolean;
  /** True once /access/me has answered. Everything works before it does. */
  settled: boolean;
  level: (key: string) => PermLevel;
  can: (key: string, need?: PermLevel) => boolean;
  /** The most open thing inside a board — what decides whether it shows in the nav. */
  reach: (key: string) => PermLevel;
  refresh: () => void;
}

const AccessContext = createContext<AccessContextValue | null>(null);

export function AccessProvider({ children }: { children: ReactNode }) {
  const { employee, accessToken } = useAuth();
  const [remote, setRemote] = useState<RemoteAccess | null>(null);
  const [nonce, setNonce] = useState(0);
  const lastKey = useRef<string>('');

  const employeeId = employee?.id ?? '';

  useEffect(() => {
    if (!accessToken || !employeeId) { setRemote(null); return; }
    const key = `${employeeId}:${nonce}`;
    if (lastKey.current === key) return;
    lastKey.current = key;

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${BASE}/access/me`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        if (!res.ok) {
          // A failed call leaves the catalogue answer standing. It is the
          // conservative one: exceptions are as likely to remove a board as to
          // add one, and nobody should lose their own board to a flaky read.
          console.log(`[access] /access/me ${res.status} — using the profile defaults`);
          return;
        }
        const json = await res.json();
        if (cancelled) return;
        setRemote({
          overrides: json.overrides ?? {},
          profileGrants: json.profileGrants ?? {},
          moneyLens: json.moneyLens ?? 'none',
          canPayPeople: !!json.canPayPeople,
        });
      } catch (err) {
        console.log(`[access] /access/me unavailable — using the profile defaults`, err);
      }
    })();

    return () => { cancelled = true; };
  }, [accessToken, employeeId, nonce]);

  const value = useMemo<AccessContextValue>(() => {
    const profile = profileForEmployee(employee);
    const source: AccessGrantSource = {
      profile,
      overrides: remote?.overrides,
      profileGrants: remote?.profileGrants,
      moneyOverride: (employee?.moneyLensOverride as MoneyLens | null | undefined) ?? null,
      // The boards that are nothing but money ask the same rung the server does.
      moneyLens: remote?.moneyLens ?? null,
    };
    return {
      source,
      profile,
      tier: profileDef(profile).tier,
      // The server's answer wins on money once it lands: a profile's rung can
      // be retuned in the database, and that is not in the bundle.
      moneyLens: remote?.moneyLens ?? moneyLensFor(source),
      canSeePay: remote ? remote.canPayPeople : canSeePayPeople(source),
      settled: remote !== null,
      level: (key) => resolveLevel(source, key),
      can: (key, need = 'read') => canInCatalog(source, key, need),
      reach: (key) => subtreeLevel(source, key),
      refresh: () => setNonce((n) => n + 1),
    };
  }, [employee, remote]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}

/**
 * Outside the provider — a lazily-mounted screen rendered before it, a test —
 * everyone is a plant-floor employee with no money. Least privilege is the only
 * safe default for a missing provider.
 */
const FALLBACK_SOURCE: AccessGrantSource = { profile: 'plant-floor' };

export function useAccess(): AccessContextValue {
  const ctx = useContext(AccessContext);
  if (ctx) return ctx;
  return {
    source: FALLBACK_SOURCE,
    profile: 'plant-floor',
    tier: 1,
    moneyLens: 'none',
    canSeePay: false,
    settled: false,
    level: (key) => resolveLevel(FALLBACK_SOURCE, key),
    can: (key, need = 'read') => canInCatalog(FALLBACK_SOURCE, key, need),
    reach: (key) => subtreeLevel(FALLBACK_SOURCE, key),
    refresh: () => {},
  };
}
